import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Download, FileText, Folder, MessageSquare, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useConversationPanel } from '../../hooks/useConversationPanel';
import { getErrorMessage } from '../../lib/apiClient';
import { formatBytes } from '../../utils/format';
import SharedFolderConversationSidebar from './components/SharedFolderConversationSidebar';
import { downloadSharedFolderFile, getSharedFolderMeta, sharedKeys } from './sharedAccessApi';

/**
 * Full-screen viewer for a folder reached via an email-share grant — the
 * folder counterpart to {@link SharedFileViewerPage}. Lists direct children
 * only (mirrors the anonymous-link and email-invite folder views, neither of
 * which support drilling into subfolders or inline preview yet), gated on
 * the `actions` the grant carries.
 */
export default function SharedFolderViewerPage() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [conversationOpen, setConversationOpen] = useConversationPanel();

  const { data: meta, isLoading, error } = useQuery({
    queryKey: sharedKeys.folderMeta(folderId),
    queryFn: () => getSharedFolderMeta(folderId),
    retry: false,
  });

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && navigate('/shared-with-me');
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const handleDownload = async (file) => {
    const toastId = toast.loading(`Downloading ${file.name}…`);
    try {
      await downloadSharedFolderFile(folderId, file.id, file.name);
      toast.success('Download ready', { id: toastId });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id: toastId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
          <Folder className="mx-auto h-10 w-10 text-slate-400" />
          <h1 className="text-lg font-semibold text-slate-800">This folder isn’t available</h1>
          <p className="text-sm text-slate-500">
            {getErrorMessage(error, 'Access may have been revoked.')}
          </p>
          <Button onClick={() => navigate('/shared-with-me')}>Back to Shared with me</Button>
        </div>
      </div>
    );
  }

  const { folder, folders, files, canDownload, canChat } = meta;

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <header className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3">
        <button
          onClick={() => navigate('/shared-with-me')}
          aria-label="Back to Shared with me"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Folder className="h-5 w-5 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">{folder.name}</p>
        </div>
        {canChat && (
          <button
            onClick={() => setConversationOpen((o) => !o)}
            aria-label="Toggle comments and chat"
            aria-pressed={conversationOpen}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-medium transition-colors ${
              conversationOpen ? 'bg-slate-200 text-slate-800' : 'text-slate-700 hover:bg-slate-200'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Comments</span>
          </button>
        )}
        <button
          onClick={() => navigate('/shared-with-me')}
          aria-label="Close viewer"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        <main className="scrollbar-thin min-h-0 flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-2xl divide-y divide-slate-100 rounded-lg border border-slate-200 bg-slate-100">
            {folders.map((f) => (
              <div key={f.id} className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
                <Folder className="h-4 w-4 text-amber-500" />
                {f.name}
              </div>
            ))}
            {files.map((file) => (
              <div key={file.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex-1 truncate text-slate-700">{file.name}</span>
                <span className="text-xs text-slate-400">{formatBytes(file.sizeBytes)}</span>
                {canDownload && (
                  <button
                    className="rounded p-1 text-slate-400 hover:text-brand-600"
                    onClick={() => handleDownload(file)}
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {folders.length === 0 && files.length === 0 && (
              <div className="px-4 py-12 text-center text-sm text-slate-400">This folder is empty.</div>
            )}
          </div>
        </main>
        {canChat && conversationOpen && <SharedFolderConversationSidebar folderId={folderId} />}
      </div>
    </div>
  );
}
